import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PhotoThumb, { PledgePhotosRow } from './PhotoThumb';

export const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c9a24d' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c9a24d', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#8a6d2f' },
};

export const listFilterFieldSx = filterFieldSx;

export const listTableHeadSx = {
  fontWeight: 800,
  fontSize: 11,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: '#64748b',
  bgcolor: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0',
  py: 1.35,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
};

export const listActionBtnSx = {
  width: 34,
  height: 34,
  borderRadius: 2,
  border: '1px solid #e2e8f0',
  bgcolor: '#fff',
};

/** Polished GIRVI detail popup — navy header, photos first, clear sections. */
export default function DetailDialog({
  open,
  onClose,
  title,
  subtitle,
  status,
  statusColor = 'success',
  children,
  maxWidth = 'md',
  headerPhotos = null,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      scroll="body"
      BackdropProps={{ sx: { bgcolor: 'rgba(15,23,42,0.55)' } }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3.5,
          overflow: 'hidden',
          border: '1px solid rgba(212,175,106,0.35)',
          boxShadow: '0 28px 80px rgba(8,15,28,0.45)',
          m: 1.5,
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(115deg, #0b1220 0%, #16233a 50%, #1e3a5f 100%)',
          px: { xs: 2, md: 2.75 },
          pt: 2,
          pb: headerPhotos ? 2 : 1.75,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            {subtitle ? (
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'rgba(245,230,200,0.7)',
                  mb: 0.4,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, color: '#f8fafc', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              {title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {status ? (
              <Chip
                size="small"
                label={String(status).toUpperCase()}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  bgcolor:
                    statusColor === 'success'
                      ? 'rgba(74,222,128,0.18)'
                      : 'rgba(248,250,252,0.12)',
                  color: statusColor === 'success' ? '#bbf7d0' : '#f8fafc',
                  border: '1px solid rgba(248,250,252,0.22)',
                }}
              />
            ) : null}
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#fff',
                color: '#0f172a',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {headerPhotos ? <Box sx={{ mt: 2 }}>{headerPhotos}</Box> : null}
      </Box>

      <Box sx={{ p: { xs: 1.25, md: 1.75 }, bgcolor: '#eef2f7', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {children}
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: '#fff',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 750,
            px: 2.75,
            py: 1,
            background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 100%)',
            boxShadow: '0 8px 18px rgba(15,23,42,0.2)',
            '&:hover': { background: 'linear-gradient(120deg, #152238 0%, #254a75 100%)' },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
}

export function DetailSection({ title, children, actions = null }) {
  return (
    <Box
      sx={{
        mb: 2,
        p: { xs: 1.75, md: 2.25 },
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        boxShadow: '0 6px 18px rgba(15,23,42,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#0f172a',
            borderLeft: '3px solid #c9a24d',
            pl: 1,
          }}
        >
          {title}
        </Typography>
        {actions}
      </Box>
      {children}
    </Box>
  );
}

export function DetailField({ label, value }) {
  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
        {value == null || value === '' ? '—' : value}
      </Typography>
    </Box>
  );
}

export function DetailFieldGrid({ items = [] }) {
  return (
    <Grid container spacing={1.75}>
      {items.map((item, idx) => (
        <Grid item xs={12} sm={6} md={item.md || 6} key={`${item.label}-${idx}`}>
          <DetailField label={item.label} value={item.value} />
        </Grid>
      ))}
    </Grid>
  );
}

/** Dark header photo strip for detail popup. */
export function DetailHeaderPhotos({ clientPhoto, articlePhoto }) {
  if (!clientPhoto && !articlePhoto) {
    return (
      <Typography sx={{ fontSize: 12, color: 'rgba(245,230,200,0.55)', fontWeight: 600 }}>
        No client / article photo on file
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
      {clientPhoto ? (
        <Box sx={{ textAlign: 'center' }}>
          <PhotoThumb src={clientPhoto} label="Client photo" size={92} />
          <Typography sx={{ mt: 0.6, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,200,0.75)' }}>
            Client
          </Typography>
        </Box>
      ) : null}
      {articlePhoto ? (
        <Box sx={{ textAlign: 'center' }}>
          <PhotoThumb src={articlePhoto} label="Article photo" size={92} />
          <Typography sx={{ mt: 0.6, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,200,0.75)' }}>
            Article
          </Typography>
        </Box>
      ) : null}
      <Typography sx={{ fontSize: 11, color: 'rgba(226,232,240,0.55)', alignSelf: 'center' }}>
        Tap photo to zoom
      </Typography>
    </Stack>
  );
}

export function DetailPhotos({ clientPhoto, articlePhoto, fingerprint = false, size = 88 }) {
  if (!clientPhoto && !articlePhoto && !fingerprint) return null;
  return (
    <DetailSection title="Photos">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
        {fingerprint ? (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: 2,
                bgcolor: '#f1f5f9',
                border: '1px solid #e2e8f0',
                display: 'grid',
                placeItems: 'center',
                color: '#64748b',
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              FP
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Fingerprint
            </Typography>
          </Box>
        ) : null}
        <PledgePhotosRow clientPhoto={clientPhoto} articlePhoto={articlePhoto} size={size} />
      </Box>
    </DetailSection>
  );
}

export { PhotoThumb, PledgePhotosRow };
